/**
 * Pulse — surveys list (HR view) + employee fill-out launchpad.
 *
 * One page, two render branches based on permission:
 *
 *   * HR_ADMIN+ → "Manage surveys" view: per-row response counts,
 *     "View results" + "Edit" actions, "New survey" CTA.
 *   * Everyone else → "Your surveys" view: surveys awaiting their
 *     response, with a "Take survey" button each.
 *
 * Both lists pull from the same /pulse/surveys endpoint. The HR
 * view additionally surfaces a link to /pulse/dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock,
  Plus,
  Send,
  Sparkles,
} from "lucide-react";

import { listSurveys, type PulseSurvey } from "@/api/pulse";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { SendSurveyModal } from "@/components/pulse/SendSurveyModal";
import { usePermissions } from "@/hooks/usePermissions";
import { pageTransition } from "@/lib/utils";

export function PulseSurveysPage() {
  const navigate = useNavigate();
  const { hasRole } = usePermissions();
  const isAdmin = hasRole("SUPER_ADMIN", "HR_ADMIN");
  // Send-controls are intentionally broader: managers can also
  // nudge their team. Employees never see this surface — they
  // only RECEIVE the survey question.
  const canSend = hasRole("SUPER_ADMIN", "HR_ADMIN", "MANAGER");
  const [sendingFor, setSendingFor] = useState<PulseSurvey | null>(null);

  const [surveys, setSurveys] = useState<PulseSurvey[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSurveys(isAdmin)
      .then((rows) => {
        if (!cancelled) setSurveys(rows);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load surveys.");
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const visible = useMemo(() => {
    if (!surveys) return null;
    // Anyone with send rights (HR_ADMIN, SUPER_ADMIN, MANAGER) sees
    // the full management list. Employees only see active surveys
    // they haven't answered yet.
    if (canSend) return surveys;
    return surveys.filter((s) => s.is_active && !s.has_responded);
  }, [surveys, canSend]);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Pulse"
        subtitle={
          canSend
            ? "Run pulse surveys, send to specific people, monitor engagement."
            : "Take a moment to share how you're feeling."
        }
        actions={
          canSend ? (
            <div className="flex gap-2">
              {isAdmin && (
                <Button
                  variant="secondary"
                  onClick={() => navigate("/pulse/dashboard")}
                >
                  <BarChart3 className="h-4 w-4" />
                  Dashboard
                </Button>
              )}
              {isAdmin && (
                <Button onClick={() => navigate("/pulse/new")}>
                  <Plus className="h-4 w-4" />
                  New survey
                </Button>
              )}
            </div>
          ) : null
        }
      />

      {error && (
        <Card className="border-danger/40 bg-danger/5">
          <p className="text-sm text-danger">{error}</p>
        </Card>
      )}

      {visible === null ? (
        <div className="grid grid-cols-1 gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState isAdmin={canSend} />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {visible.map((s) => (
            <SurveyRow
              key={s.id}
              survey={s}
              isAdmin={isAdmin}
              canSend={canSend}
              onSend={() => setSendingFor(s)}
            />
          ))}
        </div>
      )}

      {sendingFor && (
        <SendSurveyModal
          survey={sendingFor}
          onClose={() => setSendingFor(null)}
        />
      )}
    </motion.div>
  );
}


function SurveyRow({
  survey,
  isAdmin,
  canSend,
  onSend,
}: {
  survey: PulseSurvey;
  isAdmin: boolean;
  canSend: boolean;
  onSend: () => void;
}) {
  const navigate = useNavigate();
  const inactive = !survey.is_active;
  return (
    <Card className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="text-base font-semibold font-heading text-text-primary truncate">
            {survey.title}
          </h3>
          {survey.is_default_template && (
            <Badge variant="default" className="uppercase tracking-wide text-[10px]">
              Lenavio default
            </Badge>
          )}
          {survey.is_anonymous && (
            <Badge variant="info">Anonymous</Badge>
          )}
          <Badge variant="default">{survey.frequency}</Badge>
          {inactive && <Badge variant="warning">Paused</Badge>}
          {!isAdmin && survey.has_responded && (
            <Badge variant="success">
              <CheckCircle2 className="h-3 w-3" /> Done
            </Badge>
          )}
        </div>
        {survey.description && (
          <p className="text-sm text-text-muted line-clamp-2">
            {survey.description}
          </p>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-text-muted">
          <span>{survey.questions.length} question{survey.questions.length === 1 ? "" : "s"}</span>
          {isAdmin && (
            <>
              <span>·</span>
              <span>{survey.response_count} response{survey.response_count === 1 ? "" : "s"}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        {canSend ? (
          <>
            {/* Primary "Send" CTA — works for both Manager+ and
                Admin. Only this button is the new feature; the rest
                of the cluster keeps existing admin actions. */}
            <Button size="sm" onClick={onSend} disabled={!survey.is_active}>
              <Send className="h-3.5 w-3.5" />
              Send
            </Button>
            {isAdmin && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/pulse/${survey.id}/results`)}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Results
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/pulse/${survey.id}/respond`)}
            >
              Preview
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={() => navigate(`/pulse/${survey.id}/respond`)}
          >
            Take survey
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </Card>
  );
}


function EmptyState({ isAdmin }: { isAdmin: boolean }) {
  if (isAdmin) {
    return (
      <Card className="text-center py-10">
        <div className="rounded-xl bg-accent/10 mx-auto h-12 w-12 flex items-center justify-center mb-3">
          <Sparkles className="h-6 w-6 text-accent" />
        </div>
        <h3 className="text-base font-semibold text-text-primary">
          Run your first pulse
        </h3>
        <p className="text-sm text-text-muted mt-1 mb-4 max-w-md mx-auto">
          Lenavio ships three default templates: weekly pulse, monthly
          check-in, and an eNPS survey. Edit, schedule, and send.
        </p>
        <Link
          to="/pulse/new"
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          New survey
        </Link>
      </Card>
    );
  }
  return (
    <Card className="text-center py-10">
      <div className="rounded-xl bg-success/10 mx-auto h-12 w-12 flex items-center justify-center mb-3">
        <Clock className="h-6 w-6 text-success" />
      </div>
      <h3 className="text-base font-semibold text-text-primary">
        You're all caught up
      </h3>
      <p className="text-sm text-text-muted mt-1 max-w-md mx-auto">
        No pulse surveys waiting on you right now. We'll let you know when
        the next one opens up.
      </p>
    </Card>
  );
}
